// =============================================================================
// domain/mistakeTags.ts — the mistake-tag vocabulary and the controllable map.
// -----------------------------------------------------------------------------
// PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3):
// `isMistakeTagControllable`, and the `#set-controllable-tags` save loop.
//
// Two columns, and the relationship between them is the whole reason this file
// exists rather than the editor doing it inline:
//
//   `customMistakeTags`        the vocabulary — what the trade form offers
//   `controllableMistakeTags`  which of them count as a CONTROLLABLE loss
//
// ── 🚨 THE MAP IS SPARSE, AND ABSENT MEANS TRUE ──
//
// It stores only the OPT-OUTS. A tag the user is happy with has no entry at all,
// and `isControllable` returns true for anything it has never heard of — so a
// tag added on the trade form starts counting immediately without a settings
// visit. That is the old client's rule verbatim, and it is the safe direction:
// the failure mode of the opposite default is a Controllable Losses chart that
// silently reads zero.
//
// ⚠ SO TICKING A BOX DELETES A KEY. Storing `true` would work today and diverge
// the moment anything tests `'tag' in map` — and it is not what either client
// has on disk.
//
// ── 🚨 AND THE MAP IS PRUNED TO THE VOCABULARY ──
//
// The old client's save loop skips any entry whose tag is no longer in the
// vocabulary (`if (!validTagSet.has(tag)) return`). Without that, removing a tag
// leaves its opt-out behind forever — and re-adding the same tag later would
// come back silently excluded, which reads as a bug in the chart rather than in
// a settings screen the user visited months earlier.
// =============================================================================

/** The stored map: `{ tagName: false }` for opt-outs only. */
export type ControllableMap = Record<string, unknown>;

/**
 * Does a loss carrying this tag count as CONTROLLABLE?
 *
 * ⚠ `hasOwnProperty`, not truthiness. `map[tag]` is `undefined` both for a tag
 * with no entry (→ true) and for one explicitly stored as undefined, and the
 * two must not be conflated — only an explicit `false` opts out.
 */
export function isControllable(tag: string, map: ControllableMap | null | undefined): boolean {
  if (!tag) return false;
  const m = map ?? {};
  if (Object.prototype.hasOwnProperty.call(m, tag)) return m[tag] !== false;
  return true;
}

/**
 * Parse the comma-separated vocabulary box.
 *
 * ⚠ TRIMMED, EMPTIES DROPPED, DUPLICATES DROPPED — and order PRESERVED, because
 * it is the order the tags appear on the trade form. Sorting them here would
 * reshuffle a list the user has arranged.
 *
 * ⚠ Case-sensitive on purpose: the tags are the keys of the controllable map and
 * of every stored `mistakeTags` array on a trade. Folding case would orphan
 * every historical tag that differs only in capitalisation.
 */
export function parseTagVocabulary(raw: string): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const part of String(raw ?? '').split(',')) {
    const t = part.trim();
    if (!t || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  return out;
}

/** The box's contents for a stored vocabulary. */
export const formatTagVocabulary = (tags: readonly string[]): string => tags.join(', ');

/**
 * Set or clear one tag's controllable flag.
 *
 * ⚠ `true` DELETES the key rather than storing it — see the header. The map
 * holds opt-outs and nothing else.
 */
export function setControllable(
  map: ControllableMap, tag: string, controllable: boolean,
): ControllableMap {
  const next = { ...map };
  if (controllable) delete next[tag];
  else next[tag] = false;
  return next;
}

/**
 * The map with every entry whose tag has left the vocabulary removed.
 *
 * ⚠ CALLED WHENEVER THE VOCABULARY CHANGES, not only on delete. Beta prunes on
 * every save of this screen; doing it only on an explicit removal would miss the
 * case where a tag disappears because the whole box was retyped.
 */
export function pruneControllable(
  map: ControllableMap, vocabulary: readonly string[],
): ControllableMap {
  const valid = new Set(vocabulary);
  const next: ControllableMap = {};
  for (const [k, v] of Object.entries(map)) if (valid.has(k)) next[k] = v;
  return next;
}

// ---------------------------------------------------------------------------
// Unfinished positions
// ---------------------------------------------------------------------------

/** One dismissed orphan position, parsed out of its fingerprint. */
export interface SuppressedPosition {
  fingerprint: string;
  account: string;
  contract: string;
  side: string;
  qty: string;
  entry: string;
  openTime: string;
}

/**
 * Read a suppression fingerprint back into its parts.
 *
 * ⚠ THE FINGERPRINT IS THE ONLY RECORD. Beta stores a pipe-joined string —
 * `account|contract|side|openQty|avgEntry|openTime` — and nothing else, so this
 * screen can show what was dismissed only by taking it apart again. A missing
 * field comes back as an empty string and the UI prints a placeholder; it must
 * never drop the row, because an unreadable fingerprint is exactly the one a
 * user needs to be able to delete.
 */
export function parseSuppressed(fingerprint: unknown): SuppressedPosition | null {
  if (typeof fingerprint !== 'string' || !fingerprint.trim()) return null;
  const [account = '', contract = '', side = '', qty = '', entry = '', openTime = ''] =
    fingerprint.split('|');
  return { fingerprint, account, contract, side, qty, entry, openTime };
}
